/* eslint-disable @typescript-eslint/no-explicit-any */
import Highcharts from 'highcharts/esm/highstock';
import { UtilityService } from '../../../utils/utility.service';

export interface PropParams {
  propertyName: string;
  propertyValue: any;
  defaultValue: any;
}

export interface ChartMargins {
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
}

type PlotLinesItem = any;

/**
 * ChartsConfigBuilder is a helper class to build highcharts options
 */
export class ChartsConfigBuilder {

  constructor(private options?: Highcharts.Options) {}

  /**
   get options
   *
   * @returns
   */
  getConfig(): Highcharts.Options | undefined {
    return this.options;
  }

  /**
   * @param parameters An object containing the propertyName, propertyValue and defaultValue to be added
   * to the chart option object. For example,
   * setProperty({propertyName: 'xAxis.events.setExtremes', propertyValue: func, defaultValue: undefined});
   * @returns
   */
  setProperty(parameters: PropParams): ChartsConfigBuilder {
    const value = parameters.propertyValue !== undefined ? parameters.propertyValue : parameters.defaultValue;
    this.options = UtilityService.parseAndAssign({ [parameters.propertyName]: value }, this.options as any, true);
    return this;
  }

  /**
   * Accepts a variable number of arguments to insert a new series.
   *
   * If the etfirst argument is a String, then the method assumes a two argument
   * invocation where the first arg is the series name and the second is the
   * series data:
   *  => builder.addSeries('seriesName', [[1 , 2], [3, 4]]);
   *
   * If the first argument is an Object, then the method assumes a single argument
   * invocation where the first arg is an object that contains all of the series
   * data:
   *  =>  var series = { name: 'f(x)', data: [[1 , 1], [2, 4]]};
   *      builder.addSeries(series);
   *
   * @param args an array of arguments where:
   * arg1 A String representing the name of the series OR
   * a fully defined Series object
   * args2 [seriesData] an array of data.
   * @returns
   */
  addSeries(...args: Array<any>): ChartsConfigBuilder {
    this.ensureConfigProperty('series', []);

    if ('string' === typeof args[0]) {
      this.options.series.push({
        name: args[0],
        data: args[1]
      } as Highcharts.SeriesOptionsType);
    } else {
      this.options.series.push(args[0]);
    }
    return this;
  }

  /**
   * @param seriesId The name or index of the series
   * @param seriesData The series data to be updated.
   * @returns
   */
  updateSeries(seriesId: string | number, seriesData: Array<any>): ChartsConfigBuilder {
    const configSeries: Array<any> = this.options.series as Array<any>;
    const configSeriesLength = configSeries.length;
    const updateSeriesData = {

      string(): void {
        for (let i = 0; i < configSeriesLength; ++i) {
          if (configSeries[i].name === seriesId) {
            (configSeries[i]).data = seriesData;
          }
        }
      },

      number(): void {
        (configSeries[seriesId as number]).data = seriesData;
      }

    };
    if (updateSeriesData[typeof seriesId]) {
      updateSeriesData[typeof seriesId]();
    }
    return this;
  }

  /**
   * Accepts a variable number of plotlines that will be appended to the yAxis.
   * For example:
   *
   *      builder.addYAxisPlotLines({
   *               value: 5,
   *               label: {
   *                   text: 'f(x)'
   *               }
   *      });
   *
   * @param args var_args A variable number of plotLine objects.
   * @see http://api.highcharts.com/highcharts#yAxis.plotLines
   * @returns
   */
  addYAxisPlotLines(...args: Array<Record<string, unknown>>): ChartsConfigBuilder {
    this.ensureConfigProperty('yAxis.plotLines', []);
    args.forEach((item: PlotLinesItem) => {
      const yAxis = this.options.yAxis as Highcharts.AxisOptions;
      yAxis.plotLines.push(item);
    });
    return this;
  }

  /**
   * Accepts a variable number of plotlines that will be appended to the xAxis.
   * For example:
   *
   *      builder.addXAxisPlotLines({
   *               value: 5,
   *               label: {
   *                   text: 'f(x)'
   *               }
   *      });
   *
   * @param args var_args A variable number of plotLine objects.
   * @see http://api.highcharts.com/highcharts#xAxis.plotLines
   * @returns
   */
  addXAxisPlotLines(...args: Array<Record<string, unknown>>): ChartsConfigBuilder {
    this.ensureConfigProperty('xAxis.plotLines', []);
    args.forEach((item: PlotLinesItem) => {
      const xAxis = this.options.xAxis as Highcharts.AxisOptions;
      xAxis.plotLines.push(item);
    });
    return this;
  }

  /**
   * Set the dateTime label format for a specific  time unit on the xAxis.
   * For example:
   *
   *  => builder
   *      .setXAxisDateTimeLabelFormat('hour', '%l%p')
   *      .setXAxisDateTimeLabelFormat('day', '%a %b %e')
   *      .setXAxisDateTimeLabelFormat('week', '%e %b');
   *
   * @param unit
   * @param format
   * @see http://api.highcharts.com/highstock#xAxis.dateTimeLabelFormats
   * @returns
   */
  setXAxisDateTimeLabelFormat(unit: string, format: string): ChartsConfigBuilder {
    this.ensureConfigProperty('xAxis.dateTimeLabelFormats', {});
    const xAxis = this.options.xAxis as Highcharts.AxisOptions;
    xAxis.dateTimeLabelFormats[unit] = format;
    return this;
  }

  /**
   * Add a color to be used for a series.
   * For example:
   *
   *  => builder
   *      .addColor('darkgreen')
   *      .addColor('#7cb5ec');
   *
   * @param color
   * @see http://api.highcharts.com/highstock#colors
   * @returns
   */
  addColor(color: string): ChartsConfigBuilder {
    this.ensureConfigProperty('colors', []);
    this.options.colors.push(color);
    return this;
  }

  /**
   *
   * @param properties
   * The object to merge into existing xAxis properties(Existing properties get overwritten)
   * @returns The builder entity to facilitate method chaining.
   */
  extendXAxisProperties(properties: Highcharts.AxisOptions): ChartsConfigBuilder {
    Object.assign(this.options.xAxis, properties);
    return this;
  }

  /**
   *
   * @param properties xAxis is set to incoming object
   * @returns The builder entity to facilitate method chaining.
   */
  setXAxis(properties: Highcharts.XAxisOptions): ChartsConfigBuilder {
    this.options.xAxis = properties;
    return this;
  }

  /**
   *
   * @param properties The object to merge into existing yAxis properties
   (Existing properties get overwritten)
   * @returns The builder entity to facilitate method chaining.
   */
  extendYAxisProperties(properties: Highcharts.AxisOptions): ChartsConfigBuilder {
    Object.assign(this.options.yAxis, properties);
    return this;
  }

  /**
   *
   * @param properties yAxis is set to incoming object
   * @returns The builder entity to facilitate method chaining.
   */
  setYAxis(properties: Highcharts.AxisOptions): ChartsConfigBuilder {
    this.options.yAxis = properties;
    return this;
  }

  /**
   *
   * @param margins chart margins
   * @returns The builder entity to facilitate method chaining.
   */
  setChartMargins(margins: ChartMargins): ChartsConfigBuilder {
    Object.assign(this.options.chart, margins);
    return this;
  }

  /**
   *  Ensures the existence of config property. For example, assuming an empty
   *  config entity {}:
   *
   *  ensureConfigProperty('foo.bar.baz', 'qux');
   *
   *  config => {foo: {bar: {baz: "qux"}}}
   *
   * @param propertyPath The full dot notated property path
   * @param defaultValue The default value to assign to the property (assuming it is undefined)
   * @param [entity] An optional parameter for the entity that you want to apply the property
   *          path too (assumes the 'config' entity if undefined). This variable is primarily
   *          intended to facilitate recursion.
   * @returns The builder entity to facilitate method chaining.
   */
  ensureConfigProperty(propertyPath: string, defaultValue: unknown): ChartsConfigBuilder {
    this.options = UtilityService.parseAndAssign({ [propertyPath]: defaultValue }, this.options as any);
    return this;
  }
}
